// =====================================================================================
// Kitchen / body / gym mass conversion.
//
// Recipes store units as authored (lb, kg, g, oz, cups…). Display converts only
// unambiguous mass (lb ↔ kg ↔ g). Stored oz, volume, and counts pass through.
// Same-system values also pass through so "0.5 lb" stays "0.5 lb".
//
// Canonical storage: body weight in lbs, gym loads in kg, recipes as authored.
// =====================================================================================

export const GRAMS_PER_LB = 453.59237;
export const GRAMS_PER_KG = 1000;
export const GRAMS_PER_OZ = GRAMS_PER_LB / 16;
export const DEFAULT_WEIGHT_UNIT = 'lb';
export const WEIGHT_UNIT_STORAGE_KEY = 'weightUnit';

const MASS_ALIASES = {
  lb: 'lb',
  lbs: 'lb',
  pound: 'lb',
  pounds: 'lb',
  g: 'g',
  gram: 'g',
  grams: 'g',
  kg: 'kg',
  kilogram: 'kg',
  kilograms: 'kg'
};

export function resolveWeightUnit(value) {
  return value === 'kg' ? 'kg' : 'lb';
}

export function canonicalizeConvertibleMass(unit) {
  const key = String(unit || '').toLowerCase().replace(/\s+/g, ' ').trim();
  return MASS_ALIASES[key] || null;
}

export function isConvertibleMass(unit) {
  return canonicalizeConvertibleMass(unit) != null;
}

function preferredSystem(preference) {
  return resolveWeightUnit(preference) === 'kg' ? 'metric' : 'imperial';
}

function unitSystem(canonical) {
  if (canonical === 'lb') return 'imperial';
  if (canonical === 'g' || canonical === 'kg') return 'metric';
  return null;
}

export function isSameSystem(unit, preference) {
  const canonical = canonicalizeConvertibleMass(unit);
  if (!canonical) return false;
  return unitSystem(canonical) === preferredSystem(preference);
}

export function toGrams(quantity, unit) {
  const canonical = canonicalizeConvertibleMass(unit);
  const qty = Number(quantity);
  if (!canonical || !Number.isFinite(qty)) return null;
  if (canonical === 'g') return qty;
  if (canonical === 'kg') return qty * GRAMS_PER_KG;
  if (canonical === 'lb') return qty * GRAMS_PER_LB;
  return null;
}

export function fromGrams(grams, preference) {
  const pref = resolveWeightUnit(preference);
  if (!Number.isFinite(grams) || grams <= 0) {
    return { quantity: 0, unit: pref === 'kg' ? 'g' : 'lb' };
  }
  if (pref === 'kg') {
    if (grams >= GRAMS_PER_KG) {
      return { quantity: Math.round((grams / GRAMS_PER_KG) * 10) / 10, unit: 'kg' };
    }
    return { quantity: Math.round(grams), unit: 'g' };
  }
  const ounces = grams / GRAMS_PER_OZ;
  if (ounces >= 16) {
    return { quantity: Math.round((grams / GRAMS_PER_LB) * 10) / 10, unit: 'lb' };
  }
  return { quantity: Math.round(ounces), unit: 'oz' };
}

/**
 * Convert a stored quantity+unit for display.
 * Returns { quantity, unit, converted }.
 * converted is false when the value passed through unchanged.
 */
export function convertMassDisplay(quantity, unit, preference) {
  const pref = resolveWeightUnit(preference);
  const canonical = canonicalizeConvertibleMass(unit);
  if (!canonical) {
    return { quantity, unit, converted: false };
  }
  if (isSameSystem(unit, pref)) {
    return { quantity, unit, converted: false };
  }
  const grams = toGrams(quantity, unit);
  if (grams == null) {
    return { quantity, unit, converted: false };
  }
  const converted = fromGrams(grams, pref);
  return { quantity: converted.quantity, unit: converted.unit, converted: true };
}

export function formatConvertedAmount(quantity, unit) {
  const qty = Number(quantity);
  if (!Number.isFinite(qty)) return '';
  if (unit === 'lb' || unit === 'kg') {
    const rounded = Math.round(qty * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }
  return String(Math.round(qty));
}

export function massAggregationKey(name, unit) {
  const normalizedName = String(name || '').toLowerCase();
  if (isConvertibleMass(unit)) {
    return `${normalizedName}_mass`;
  }
  return `${normalizedName}_${String(unit || '').toLowerCase()}`;
}

export function ingredientUnitsMatch(left, right) {
  if (isConvertibleMass(left) && isConvertibleMass(right)) return true;
  return String(left || '').toLowerCase() === String(right || '').toLowerCase();
}

export function formatMassDisplayText(quantity, unit, name, preference) {
  const converted = convertMassDisplay(Number(quantity), unit, preference);
  if (!converted.converted) {
    return `${quantity} ${unit || ''} ${name || ''}`.replace(/\s+/g, ' ').trim();
  }
  return `${formatConvertedAmount(converted.quantity, converted.unit)} ${converted.unit} ${name || ''}`.replace(/\s+/g, ' ').trim();
}

export function lbsToDisplayWeight(lbs, preference) {
  const value = Number(lbs);
  if (!Number.isFinite(value) || value <= 0) {
    return { value: '', unit: resolveWeightUnit(preference) };
  }
  if (resolveWeightUnit(preference) !== 'kg') {
    return { value, unit: 'lb' };
  }
  return { value: Math.round(value * (GRAMS_PER_LB / GRAMS_PER_KG) * 10) / 10, unit: 'kg' };
}

export function displayWeightToLbs(value, preference) {
  const qty = Number(value);
  if (!Number.isFinite(qty) || qty <= 0) return undefined;
  if (resolveWeightUnit(preference) !== 'kg') {
    return Math.round(qty * 10) / 10;
  }
  return Math.round((qty / (GRAMS_PER_LB / GRAMS_PER_KG)) * 10) / 10;
}

export function gymKgToDisplay(kg, preference) {
  const value = Number(kg);
  if (!Number.isFinite(value) || value <= 0) {
    return { value: '', unit: resolveWeightUnit(preference) === 'lb' ? 'lb' : 'kg' };
  }
  if (resolveWeightUnit(preference) !== 'lb') {
    return { value, unit: 'kg' };
  }
  return { value: Math.round((value / (GRAMS_PER_LB / GRAMS_PER_KG)) * 10) / 10, unit: 'lb' };
}

export function displayToGymKg(value, preference) {
  const qty = Number(value);
  if (!Number.isFinite(qty) || qty <= 0) return null;
  if (resolveWeightUnit(preference) !== 'lb') {
    return qty;
  }
  return Math.round(qty * (GRAMS_PER_LB / GRAMS_PER_KG) * 100) / 100;
}

export function bodyWeightInputBounds(preference) {
  if (resolveWeightUnit(preference) === 'kg') {
    return { min: 23, max: 227, placeholder: 'e.g. 77' };
  }
  return { min: 50, max: 500, placeholder: 'e.g. 170' };
}

export function convertDisplayWeight(value, fromUnit, toUnit) {
  if (fromUnit === toUnit) return value;
  const lbs = displayWeightToLbs(value, fromUnit);
  if (lbs == null) return '';
  return lbsToDisplayWeight(lbs, toUnit).value;
}

/** Accumulate a scaled ingredient into a shopping map, merging convertible mass. */
export function accumulateIngredient(map, name, quantity, unit, extra = {}) {
  const key = massAggregationKey(name, unit);
  const grams = toGrams(quantity, unit);
  const canonical = canonicalizeConvertibleMass(unit);
  if (grams != null) {
    if (map.has(key)) {
      const existing = map.get(key);
      existing.grams += grams;
      existing.canonicals.add(canonical);
      if (extra.source && existing.sources instanceof Set) existing.sources.add(extra.source);
      return;
    }
    map.set(key, {
      key,
      name,
      grams,
      mass: true,
      canonicals: new Set([canonical]),
      quantity,
      unit,
      ...extra
    });
    return;
  }
  if (map.has(key)) {
    const existing = map.get(key);
    existing.quantity += quantity || 0;
    if (extra.source && existing.sources instanceof Set) existing.sources.add(extra.source);
    return;
  }
  map.set(key, {
    key,
    name,
    quantity: quantity || 0,
    unit,
    ...extra
  });
}

export function finalizeAggregatedItem(item, preference) {
  if (!item || !item.mass) return item;
  const pref = resolveWeightUnit(preference);
  const allSameSystem = item.canonicals
    && [...item.canonicals].every((canonical) => unitSystem(canonical) === preferredSystem(pref));
  const converted = allSameSystem && pref === 'lb'
    ? { quantity: Math.round((item.grams / GRAMS_PER_LB) * 10) / 10, unit: 'lb' }
    : fromGrams(item.grams, pref);
  return {
    ...item,
    quantity: converted.quantity,
    unit: converted.unit
  };
}
